import type { ErrorEvent } from '@sentry/nuxt'

/**
 * Boundary context for errors caught by `ErrorBoundary`.
 *
 * `NuxtErrorBoundary` forwards every error it catches to the `vue:error` hook,
 * where @sentry/nuxt captures it. That capture carries no boundary context and
 * is flagged `handled: false`, so contained errors look like hard crashes and
 * group by minified stack. Rather than capturing a second, richer event (and
 * double-reporting), `ErrorBoundary` stamps the error object with this metadata
 * before it reaches the hook, and `sentry.client.config.ts` reads it back in
 * `beforeSend` to enrich the single event Sentry already builds.
 */
export const BOUNDARY_META_KEY = '__kftBoundaryMeta'

/** Message substring in `fingerprint`, mirroring `sentry-console-override.ts`. */
const FINGERPRINT_MESSAGE_MAX_LENGTH = 100

export interface BoundaryErrorMeta {
  /** Component hosting the boundary, e.g. `EventBuyBoxHeader`. */
  boundary: string
  /** Route name without the locale suffix, e.g. `e-eventPermalink`. */
  routeName: string
  /** Dynamic import failure — these trigger a page reload. */
  isChunkLoadError: boolean
}

/**
 * Stamps boundary metadata onto an error as a non-enumerable property, so it
 * never leaks into serialized error output or Sentry's `extra` payload.
 */
export const attachBoundaryMeta = (
  error: Error,
  meta: BoundaryErrorMeta
): void => {
  try {
    Object.defineProperty(error, BOUNDARY_META_KEY, {
      value: meta,
      enumerable: false,
      configurable: true,
      writable: true,
    })
  } catch {
    // Frozen or non-extensible error: enrichment is best-effort, never fatal.
  }
}

export const readBoundaryMeta = (
  error: unknown
): BoundaryErrorMeta | undefined => {
  if (!error || typeof error !== 'object') return undefined

  return (error as Record<string, BoundaryErrorMeta | undefined>)[
    BOUNDARY_META_KEY
  ]
}

/**
 * Adds boundary tags and a stable fingerprint to a Sentry event, and clears the
 * `handled: false` flag @sentry/nuxt sets on every `vue:error` report — the
 * boundary contained this error, so it is not an unhandled crash.
 *
 * The fingerprint keys off the route name rather than a file name because
 * minified file names change on every deploy, which would split one issue into
 * a new one per release. Same reasoning as `sentry-console-override.ts`.
 */
export const enrichBoundaryEvent = (
  event: ErrorEvent,
  meta: BoundaryErrorMeta,
  errorMessage?: string
): ErrorEvent => {
  event.tags = {
    ...event.tags,
    source: 'error-boundary',
    boundary: meta.boundary,
    routeName: meta.routeName,
    chunkLoadError: meta.isChunkLoadError,
  }

  // The last entry is the error itself; anything before it is a linked cause.
  const values = event.exception?.values
  const originalException = values?.[values.length - 1]

  const message = errorMessage ?? originalException?.value ?? 'unknown'

  event.fingerprint = [
    'error-boundary',
    meta.boundary,
    meta.routeName,
    message.substring(0, FINGERPRINT_MESSAGE_MAX_LENGTH),
  ]

  if (originalException?.mechanism) {
    originalException.mechanism.handled = true
  }

  return event
}
